import { useEffect, useRef, useState } from "react";
import { Users, Crosshair, Flag, RotateCcw, Heart, ChevronRight, Zap } from "lucide-react";
import { RoadEngine } from "./roadEngine";
import { spaceApi } from "./spaceApi";
import { SpaceHomeBtn } from "./SpaceNav";

// Stage art (self-hosted transparent sprites + backdrops in public/game/road).
const A = "/game/road";
const STAGE_BGS = { 1: `${A}/bg_stage1.jpg`, 2: `${A}/bg_stage2.jpg`, 3: `${A}/bg_stage3.jpg` };
const HERO_SPRITES = { jess: `${A}/hero_jess.png`, bazooka: `${A}/hero_bazooka.png`, nancy: `${A}/hero_nancy.png` };
const BOSS_SPRITE = `${A}/boss_leviathan.png`;

const STAGE_NAME = { 1: "The Startup Run", 2: "The Danger Highway", 3: "The Alien Nexus" };

const INTERSTITIAL = {
  1: { title: "STAGE 1 CLEARED", area: "AREA 2 · DANGER HIGHWAY", icon: "⚠️", tag: "NEW HAZARD DETECTED",
       body: "Red window gates will strip your fire-rate tier. Shoot them to flip them BLUE before you cross.", cta: "Advance" },
  2: { title: "STAGE 2 CLEARED", area: "AREA 3 · THE ALIEN NEXUS", icon: "👾", tag: "CONVERGING SWARMS ONLINE",
       body: "Enemies now track your movement diagonally, and an Elite Leviathan waits ahead. Protect your heroes at all costs.", cta: "Engage Elite Target" },
};

// Road Shooter — 3-stage campaign (Jess + GI Bazooka → Nancy rescue → Boss).
export default function RoadShooter({ onBack }) {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const [hud, setHud] = useState({ troops: 0, weaponTier: 1, lives: 3, stage: 1, progress: 0, boss: null });
  const [phase, setPhase] = useState("playing"); // playing | interstitial | rescue | gameover | victory
  const [fromStage, setFromStage] = useState(1);
  const [result, setResult] = useState(null);
  const [reward, setReward] = useState(null);
  const [runId, setRunId] = useState(0);

  useEffect(() => {
    setPhase("playing"); setResult(null); setReward(null);
    setHud({ troops: 0, weaponTier: 1, lives: 3, stage: 1, progress: 0, boss: null });
    const eng = new RoadEngine(canvasRef.current, {
      campaign: true,
      heroSprites: HERO_SPRITES, bossSprite: BOSS_SPRITE, stageBackdrops: STAGE_BGS,
      rescueSprite: "/game/bunker/med_bay.png",
      onUpdate: (s) => setHud(s),
      onStageClear: (stage) => { setFromStage(stage); setPhase("interstitial"); },
      onRescue: () => setPhase("rescue"),
      onFinish: (r) => {
        setResult({ win: true, ...r }); setPhase("victory"); setReward("pending");
        spaceApi.roadPatrolClaim(r.troops, r.weaponLevel).then((d) => setReward(d.credited)).catch(() => setReward(null));
      },
      onGameOver: (r) => { setResult({ win: false, ...r }); setPhase("gameover"); },
    });
    engineRef.current = eng;
    eng.mount();
    const onResize = () => eng.resize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); eng.unmount(); };
  }, [runId]);

  const advance = () => { engineRef.current?.advanceStage(); setPhase("playing"); };
  const deployNancy = () => { engineRef.current?.completeRescue(); setPhase("playing"); };
  const replay = () => setRunId((n) => n + 1);

  const inter = INTERSTITIAL[fromStage];

  return (
    <div className="ss-viewport rp-viewport" data-testid="road-shooter-app">
      <canvas ref={canvasRef} className="ss-canvas" data-testid="road-shooter-canvas" />

      <SpaceHomeBtn onClick={onBack} testid="road-shooter-back" />

      {/* Top status HUD */}
      <div className="rp-hud" data-testid="road-hud">
        <div className="rp-stat" data-testid="road-stage"><b>S{hud.stage}</b><span>{STAGE_NAME[hud.stage]}</span></div>
        <div className="rp-stat" data-testid="road-troops"><Users size={14} /> <b>{hud.troops}</b><span>/30</span></div>
        <div className="rp-stat wpn" data-testid="road-weapon"><Crosshair size={14} /> T<b>{hud.weaponTier}</b></div>
        <div className="rp-lives" data-testid="road-lives">
          {[0, 1, 2].map((i) => (
            <Heart key={i} size={15} fill={i < hud.lives ? "#ff4560" : "none"} stroke={i < hud.lives ? "#ff4560" : "#5b6b82"} />
          ))}
        </div>
        <div className="rp-progress"><span style={{ width: `${hud.progress * 100}%` }} /><Flag size={12} className="rp-flag" /></div>
      </div>

      {phase === "playing" && (
        <div className="rp-hint" data-testid="road-hint">Drag to steer · shoot red gates to flip them · grab troops · dodge barrels</div>
      )}

      {/* Stage interstitial */}
      {phase === "interstitial" && inter && (
        <div className="ss-overlay rp-inter" data-testid="road-interstitial">
          <span className="rp-inter-flag">🚩 {inter.title}</span>
          <span className="rp-inter-area">{inter.area}</span>
          <div className="rp-inter-card">
            <span className="rp-inter-tag">{inter.icon} {inter.tag}</span>
            <p>{inter.body}</p>
          </div>
          <button className="ss-enter-btn" data-testid="road-interstitial-advance" onClick={advance}>
            {inter.cta} <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Nancy rescue */}
      {phase === "rescue" && (
        <div className="ss-overlay rp-rescue" data-testid="road-rescue">
          <span className="rp-rescue-siren">🚨 HERO RESCUE INBOUND 🚨</span>
          <span className="ov-t">NANCY · Biotech Medic</span>
          <span className="ov-s">Heavy ordnance online. She'll drop into the squad beside Jess and fight to the end of the run.</span>
          <button className="ss-enter-btn" data-testid="road-rescue-deploy" onClick={deployNancy}>
            <Zap size={16} /> Deploy Nancy
          </button>
        </div>
      )}

      {/* Victory / Game Over */}
      {(phase === "victory" || phase === "gameover") && result && (
        <div className={`ss-overlay ${result.win ? "win" : "lose"}`} data-testid={result.win ? "road-finish" : "road-gameover"}>
          <span className="ov-t">{result.win ? "Nexus Cleared" : "Squad Wiped"}</span>
          <span className="ov-s">
            {result.win ? `The Leviathan is down. Arrived with ${result.troops} troops · Weapons T${Math.min(3, result.weaponLevel)}` : "The squad was overrun. Regroup and run it back."}
          </span>
          {result.win && (
            <div className="rp-ledger" data-testid="road-ledger">
              {reward === "pending" && <span className="rp-ledger-wait">Banking rewards…</span>}
              {reward && reward !== "pending" && (
                <>
                  <span className="rp-earn ti" data-testid="road-earn-titanium">+{reward.titanium} Titanium</span>
                  <span className="rp-earn al" data-testid="road-earn-alloy">+{reward.micro_alloy} Micro-Alloy</span>
                </>
              )}
              {reward === null && <span className="rp-ledger-wait">Rewards could not be banked</span>}
            </div>
          )}
          <button className="ss-enter-btn" data-testid="road-replay" onClick={replay}>
            <RotateCcw size={16} /> {result.win ? "Play Again" : "Retry"}
          </button>
        </div>
      )}
    </div>
  );
}
